import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Facebook,
  Instagram,
  HeartHandshake,
  CalendarCheck,
  Mail,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import TermsModal from './TermsModal';
import ReviewCarousel from './ReviewCarousel';
import {
  fadeInUp,
  staggerContainer,
  floatAnimation,
  defaultTransition,
} from '../utils/animations';

interface Review {
  id: string;
  content: string;
  rating: number;
  profiles: {
    prenume: string;
    nume: string;
  };
}

export default function Hero() {
  const [showTerms, setShowTerms] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select(
          `
          id,
          content,
          rating,
          profiles:profiles!inner (
            prenume,
            nume
          )
        `
        )
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      // Transform the data to match our Review interface
      const transformedData = (data || []).map((review: {
        id: string;
        content: string;
        rating: number;
        profiles: { prenume: string; nume: string } | { prenume: string; nume: string }[];
      }) => ({
        id: review.id,
        content: review.content,
        rating: review.rating,
        profiles: Array.isArray(review.profiles)
          ? review.profiles[0]
          : review.profiles,
      }));

      setReviews(transformedData);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  return (
    <div className="relative bg-gradient-to-b from-[#024994] via-[#012a52] to-[#0A0E27] text-white overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Orbs */}
        <motion.div
          className="absolute top-20 left-10 w-80 h-80 bg-cyan-500/20 rounded-full blur-3xl"
          {...floatAnimation}
        />
        <motion.div
          className="absolute top-32 right-1/4 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl"
          animate={{
            y: [100, -50, 100],
            transition: {
              duration: 16,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/3 w-72 h-72 bg-purple-500/8 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
            transition: {
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-64 h-64 bg-cyan-400/15 rounded-full blur-3xl"
          animate={{
            y: [-15, 15, -15],
            x: [-10, 10, -10],
            transition: {
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute top-3/4 left-16 w-48 h-48 bg-purple-600/10 rounded-full blur-3xl"
          animate={{
            scale: [0.8, 1.3, 0.8],
            opacity: [0.2, 0.4, 0.2],
            transition: {
              duration: 14,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute bottom-32 left-1/2 w-56 h-56 bg-blue-600/12 rounded-full blur-3xl"
          {...floatAnimation}
          animate={{
            y: [-10, 10, -10],
            transition: {
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1,
            },
          }}
        />
        <motion.div
          className="absolute top-16 right-16 w-40 h-40 bg-cyan-300/15 rounded-full blur-3xl"
          animate={{
            y: [10, 200, 10],
            rotate: [0, 360],
            transition: {
              duration: 20,
              repeat: Infinity,
              ease: 'linear',
            },
          }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/3 w-32 h-32 bg-purple-400/8 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.4, 1],
            opacity: [0.2, 0.6, 0.2],
            transition: {
              duration: 18,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:50px_50px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]" />

        {/* Particle Effect */}
        {[...Array(50)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400/40 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0.1, 1, 0.1],
              transition: {
                duration: 4 + Math.random() * 4,
                repeat: Infinity,
                delay: Math.random() * 4,
                ease: 'easeInOut',
              },
            }}
          />
        ))}

        {/* Additional Particle Layers */}
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={`layer2-${i}`}
            className="absolute w-0.5 h-0.5 bg-purple-300/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [-50, 50, -50],
              x: [-30, 30, -30],
              opacity: [0.2, 0.8, 0.2],
              transition: {
                duration: 6 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 2,
                ease: 'easeInOut',
              },
            }}
          />
        ))}

        {/* Small Stars/Light Dots */}
        {[...Array(25)].map((_, i) => (
          <motion.div
            key={`stars-${i}`}
            className="absolute w-0.5 h-0.5 bg-white/60 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              scale: [0.5, 1.2, 0.5],
              opacity: [0.3, 0.9, 0.3],
              transition: {
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 3,
                ease: 'easeInOut',
              },
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div
          className="py-28 text-center"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          {/* Farewell Icon */}
          <motion.div className="flex justify-center mb-8" variants={fadeInUp}>
            <div className="bg-gradient-to-br from-cyan-400 to-blue-500 w-20 h-20 rounded-2xl flex items-center justify-center shadow-2xl">
              <HeartHandshake className="w-10 h-10 text-white" />
            </div>
          </motion.div>

          <motion.div
            className="relative inline-block max-w-5xl mx-auto mb-6"
            variants={fadeInUp}
          >
            <h1 className="relative text-4xl md:text-6xl font-bold text-white p-8">
              Mulțumim. Aici se încheie drumul nostru.
            </h1>
          </motion.div>

          {/* Announcement Card */}
          <motion.div
            className="max-w-3xl mx-auto bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-8 text-left space-y-5"
            variants={fadeInUp}
          >
            <p className="text-xl text-[#D3D1CF] leading-relaxed">
              Cu mare regret vă anunțăm că proiectul{' '}
              <span className="font-semibold text-white">
                Decizie Oncologică
              </span>{' '}
              își încetează activitatea.
            </p>
            <p className="text-lg text-[#D3D1CF] leading-relaxed">
              Nu mai reușim să găsim puterea și resursele necesare pentru a
              menține platforma în funcțiune, iar a continua la jumătate de
              măsură ar însemna să nu fim corecți față de pacienții care au
              nevoie de noi.
            </p>
            <p className="text-lg text-[#D3D1CF] leading-relaxed">
              <span className="font-semibold text-white">
                Ne pare sincer rău.
              </span>{' '}
              Mulțumim din suflet fiecărui pacient care ne-a acordat încrederea
              sa și fiecărui medic care a dăruit din timpul său acestei idei.
            </p>
          </motion.div>

          {/* What happens next */}
          <motion.div
            className="max-w-3xl mx-auto mt-8 bg-white/5 backdrop-blur-lg rounded-xl border border-white/10 p-8 text-left"
            variants={fadeInUp}
          >
            <div className="flex items-start gap-4">
              <CalendarCheck className="w-7 h-7 text-cyan-400 flex-shrink-0 mt-1" />
              <div className="space-y-4">
                <p className="text-lg text-[#D3D1CF] leading-relaxed">
                  Platforma rămâne activă{' '}
                  <span className="font-semibold text-white">
                    inclusiv în luna septembrie
                  </span>
                  .
                </p>
                <p className="text-lg text-[#D3D1CF] leading-relaxed">
                  După această dată, pacienții care doresc re-emiterea scrisorii
                  medicale o pot solicita pe adresa de e-mail de mai jos.
                </p>
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                  <a
                    href="mailto:admin@decizieoncologica.ro"
                    className="text-cyan-400 hover:text-cyan-300 underline transition-colors text-lg font-semibold break-all"
                  >
                    admin@decizieoncologica.ro
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Reviews Section */}
        {reviews.length > 0 && (
          <motion.div
            className="py-16 relative"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
          >
            <div className="absolute inset-0 bg-white/5 backdrop-blur-xl rounded-xl border border-white/10" />
            <div className="relative p-8">
              <motion.h2
                className="text-2xl font-semibold text-center mb-3"
                variants={fadeInUp}
              >
                Mulțumim pentru cuvintele voastre
              </motion.h2>
              <motion.p
                className="text-center text-[#D3D1CF] mb-12"
                variants={fadeInUp}
              >
                Câteva dintre gândurile pacienților care au trecut pe la noi.
                Ele rămân cea mai frumoasă amintire a acestui proiect.
              </motion.p>
              <ReviewCarousel reviews={reviews} />
            </div>
          </motion.div>
        )}

        {/* Footer */}
        <motion.footer
          className="bg-white/5 backdrop-blur-lg py-6 mt-20 rounded-t-xl border-t border-white/10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={defaultTransition}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-4">
                <motion.img
                  src="/logo.png"
                  alt="Decizie Oncologica"
                  className="h-12 w-auto object-contain"
                  whileHover={{ scale: 1.05 }}
                />
                <div className="flex items-center gap-2">
                  <motion.a
                    href="https://facebook.com/decizieoncologica/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:text-cyan-400 transition p-2"
                    aria-label="Facebook"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Facebook className="w-5 h-5" />
                  </motion.a>
                  <motion.a
                    href="https://instagram.com/decizieoncologica/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:text-cyan-400 transition p-2"
                    aria-label="Instagram"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Instagram className="w-5 h-5" />
                  </motion.a>
                </div>
              </div>
              <div className="flex-1 flex justify-center">
                <motion.button
                  onClick={() => setShowTerms(true)}
                  className="text-white hover:text-cyan-400 transition"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Termeni și condiții
                </motion.button>
              </div>
              <div className="flex items-center gap-4">
                <motion.img
                  src="/netopia.png"
                  alt="Netopia Payments"
                  className="h-12 object-contain"
                  whileHover={{ scale: 1.05 }}
                />
                <motion.a
                  href="https://reclamatiisal.anpc.ro/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition"
                  whileHover={{ scale: 1.05 }}
                >
                  <img
                    src="/anpc_sal.svg"
                    alt="ANPC SAL"
                    className="h-12 object-contain"
                  />
                </motion.a>
                <motion.a
                  href="https://ec.europa.eu/consumers/odr/main/index.cfm?event=main.home.chooseLanguage"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition"
                  whileHover={{ scale: 1.05 }}
                >
                  <img
                    src="/anpc_sol.svg"
                    alt="ANPC SOL"
                    className="h-12 object-contain"
                  />
                </motion.a>
              </div>
            </div>
          </div>
        </motion.footer>
      </div>

      <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
    </div>
  );
}
